import * as React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight, X } from 'lucide-react'
import { AppLink } from '@/components/ui/app-link'
import { LazyPlatformPlayer } from '@/components/features/media/player/LazyPlatformPlayer'
import type { PlatformPlayerProps } from '@/components/features/media/player/playerTypes'
import { cn } from '@/lib/utils'
import { useOptionalPlayer } from './PlayerProvider'

/** What a page hands the dock to play: the video and who hears about its playhead. */
export interface DockVideo {
  /** The episode id: loading the same key again keeps the element (and its playhead). */
  key: string
  title: string
  /** The episode page, for the floating window's link back. */
  href: string | null
  src: { url: string; type?: string }
  hls?: { manifestUrl: string; params: Record<string, string> | null } | null
  poster?: string | null
  tracks?: PlatformPlayerProps['tracks']
  resumeAt?: number
  onTime?: (seconds: number, duration?: number) => void
  onPause?: (seconds: number, duration?: number) => void
  onEnded?: () => void
}

interface VideoDockApi {
  /** Plays `video` in the page's slot; the same `key` only refreshes links and handlers. */
  load: (video: DockVideo) => void
  /** The page's slot (its ref callback); `null` when the page leaves. */
  attach: (slot: HTMLElement | null) => void
  element: () => HTMLVideoElement | null
}

const VideoDockContext = React.createContext<VideoDockApi | null>(null)

/** The video dock of the community session, or `null` outside it. */
export const useVideoDock = () => React.useContext(VideoDockContext)

/** A slot already left counts as left after this (theater mode moves the slot within one page). */
const DETACH_GRACE_MS = 80

/**
 * F-178 criterion 8: one `<video>` per community session that outlives the module switch. The
 * episode page lends it a slot; leaving the page while it plays moves the same element to a small
 * floating window (title back to the episode, close) and asks the browser for Picture-in-Picture
 * when it allows. It never plays podcast audio: starting a video pauses the podcast player.
 * Mounted inside `PlayerProvider`, so another community or account remounts it (stops).
 */
export function VideoDockProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('player')
  const podcast = useOptionalPlayer()
  const podcastRef = React.useRef(podcast)
  podcastRef.current = podcast
  const [video, setVideo] = React.useState<DockVideo | null>(null)
  const [slot, setSlot] = React.useState<HTMLElement | null>(null)
  const [floating, setFloating] = React.useState(false)
  const [inPip, setInPip] = React.useState(false)
  /* HLS that cannot run here (or failed for good) falls back to the file, per video. */
  const [hlsOff, setHlsOff] = React.useState<string | null>(null)
  const host = React.useMemo(() => {
    const el = document.createElement('div')
    el.className = 'h-full w-full'
    return el
  }, [])
  const parking = React.useRef<HTMLDivElement | null>(null)
  const windowRef = React.useRef<HTMLDivElement | null>(null)
  const videoEl = React.useRef<HTMLVideoElement | null>(null)
  const videoRef = React.useRef<DockVideo | null>(null)
  videoRef.current = video
  const detachTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const resumeRef = React.useRef({ at: 0, playing: false })
  const paramsRef = React.useRef<Record<string, string> | null>(null)
  paramsRef.current = video?.hls?.params ?? null

  const load = React.useCallback((next: DockVideo) => {
    setVideo((current) => {
      if (current?.key === next.key) return { ...next }
      resumeRef.current = { at: next.resumeAt ?? 0, playing: false }
      return next
    })
  }, [])

  const close = React.useCallback(() => {
    videoEl.current?.pause()
    if (document.pictureInPictureElement) void document.exitPictureInPicture().catch(() => undefined)
    setFloating(false)
    setVideo(null)
  }, [])

  const attach = React.useCallback((next: HTMLElement | null) => {
    if (detachTimer.current) { clearTimeout(detachTimer.current); detachTimer.current = null }
    if (next) {
      /* Straight in, in the same task: a media element out of the document for a task pauses. */
      if (host.parentElement !== next) next.appendChild(host)
      setSlot(next)
      setFloating(false)
      return
    }
    /* Leaving: the slot goes with the page, so the element moves to the parking spot now (same task,
       no pause); within the grace a new slot (theater mode) takes it; after it, float or stop. */
    const el0 = videoEl.current
    const wasPlaying = Boolean(el0 && !el0.paused && !el0.ended)
    if (parking.current) parking.current.appendChild(host)
    detachTimer.current = setTimeout(() => {
      detachTimer.current = null
      setSlot(null)
      const el = videoEl.current
      if (el && wasPlaying) {
        if (el.paused) void el.play().catch(() => undefined)
        setFloating(true)
        if (document.pictureInPictureEnabled && !el.disablePictureInPicture && !document.pictureInPictureElement) {
          void el.requestPictureInPicture().catch(() => undefined)
        }
      } else {
        setFloating(false)
        setVideo(null)
      }
    }, DETACH_GRACE_MS)
  }, [host])

  /* The one element moves between the page's slot, the floating window and a hidden parking spot. */
  React.useLayoutEffect(() => {
    const target = slot ?? (floating ? windowRef.current : null) ?? parking.current
    if (target && host.parentElement !== target) target.appendChild(host)
  }, [slot, floating, host, video])

  /* The podcast and a video never play together. */
  React.useEffect(() => {
    if (podcast?.status === 'playing' && videoEl.current && !videoEl.current.paused) videoEl.current.pause()
  }, [podcast?.status])

  React.useEffect(() => () => { if (detachTimer.current) clearTimeout(detachTimer.current) }, [])

  const bindElement = React.useCallback((el: HTMLVideoElement | null) => {
    videoEl.current = el
    if (!el) return
    const enter = () => setInPip(true)
    const leave = () => setInPip(false)
    el.addEventListener('enterpictureinpicture', enter)
    el.addEventListener('leavepictureinpicture', leave)
  }, [])

  const api = React.useMemo<VideoDockApi>(() => ({ load, attach, element: () => videoEl.current }), [load, attach])
  const showWindow = Boolean(video) && floating && !slot

  return (
    <VideoDockContext.Provider value={api}>
      {children}
      <div ref={parking} hidden aria-hidden />
      {video && createPortal(
        <LazyPlatformPlayer
          key={`${video.key}-${hlsOff === video.key ? 'mp4' : 'hls'}`}
          src={video.src}
          hls={video.hls && hlsOff !== video.key ? { manifestUrl: video.hls.manifestUrl, paramsRef } : null}
          onHlsUnusable={() => setHlsOff(video.key)}
          /* No loader for the stream here (a browser without its codec): the original file instead. */
          onFailure={() => { if (video.hls && hlsOff !== video.key) setHlsOff(video.key) }}
          poster={video.poster}
          title={video.title}
          ratio={{ w: 16, h: 9 }}
          className="w-full"
          resumeRef={resumeRef}
          tracks={video.tracks}
          onVideoElement={bindElement}
          onPlay={() => { if (podcastRef.current?.status === 'playing') podcastRef.current.pause() }}
          onTimeUpdate={(s, d) => videoRef.current?.onTime?.(s, d)}
          onPause={(s, d) => videoRef.current?.onPause?.(s, d)}
          onEnded={() => videoRef.current?.onEnded?.()}
        />,
        host
      )}
      <div
        className={cn(
          'fixed right-3 z-30 w-[min(20rem,calc(100%-1.5rem))] overflow-hidden rounded-xl bg-black shadow-2xl ring-1 ring-black/10 sm:right-4',
          showWindow && !inPip ? 'block' : 'pointer-events-none fixed -left-[9999px] opacity-0'
        )}
        style={{ bottom: 'calc(var(--player-h, 0px) + 0.75rem)' }}
        role="region"
        aria-label={t('videoDock.label')}
        aria-hidden={!showWindow || inPip}
        data-testid="video-dock"
      >
        <div className="flex items-center gap-2 px-3 py-2 text-white">
          {video?.href ? (
            <AppLink to={video.href} className="flex min-w-0 flex-1 items-center gap-1 text-sm font-medium hover:underline" title={t('videoDock.back')} data-testid="video-dock-link">
              <span className="truncate">{video.title}</span><ArrowUpRight className="h-4 w-4 flex-shrink-0" aria-hidden />
            </AppLink>
          ) : <span className="min-w-0 flex-1 truncate text-sm font-medium">{video?.title}</span>}
          <button type="button" onClick={close} className="rounded p-1 hover:bg-surface/10" aria-label={t('videoDock.close')} data-testid="video-dock-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div ref={windowRef} className="aspect-video w-full" />
      </div>
    </VideoDockContext.Provider>
  )
}
